import { useMutation, useQuery } from '@apollo/client'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import styled from 'styled-components'

import {
  CustomerAddressInput,
  GetCountriesDocument,
  GetCustomerAddressesDocument,
  UpdateCustomerAddressDocument,
} from '../../../../graphql/magento'
import { TokenContext } from '../../../../layouts/context'
import { usePageUrl } from '../../../../lib/pages'
import { checkAndDiscardQueryResponse } from '../../../../utils/graphqlTools'
import Spinner from '../../../Spinner'
import QueryFallback from '../QueryFallback'
import { AddressForm } from './AddressForm'

export interface EditAddressContainerProps {
  addressId: string
  className?: string
}

const EditAddressContainer: React.FC<EditAddressContainerProps> = ({ addressId, className }) => {
  const token = useContext(TokenContext)
  const [saveDisabled, setSaveDisabled] = useState(false)
  const { navigate } = usePageUrl()

  const {
    data: countriesData,
    error: countriesError,
    loading: countriesLoading,
    refetch: countriesRefetch,
  } = useQuery(GetCountriesDocument)

  const { loading: addressesLoading, data: addressesData } = useQuery(
    GetCustomerAddressesDocument,
    {
      context: { token },
      notifyOnNetworkStatusChange: true,
      skip: !token,
    },
  )

  const [updateCustomerAddress] = useMutation(UpdateCustomerAddressDocument, {
    context: { token },
    refetchQueries: [
      {
        query: GetCustomerAddressesDocument,
        context: { token },
      },
    ],
  })

  const address = useMemo(
    () =>
      addressesData?.customer?.addresses?.find(
        (addressData) => `${addressData?.id ?? ''}` === addressId,
      ),
    [addressId, addressesData?.customer?.addresses],
  )

  const handleSaveAddress = useCallback(
    async (addressInput: CustomerAddressInput): Promise<void> => {
      setSaveDisabled(true)
      await updateCustomerAddress({
        variables: { id: Number(addressId), input: addressInput },
      })
        .then(checkAndDiscardQueryResponse)
        .then(() => {
          navigate('/account/addresses')
        })
        .catch((_err) => {
          setSaveDisabled(false)
        })
    },
    [updateCustomerAddress, addressId, navigate],
  )

  return (
    <div className={className}>
      {!!countriesError && !countriesLoading && (
        <QueryFallback
          buttonText="Retry Loading Countries"
          error={countriesError}
          onRetry={countriesRefetch}
        />
      )}
      <Spinner loading={countriesLoading || addressesLoading} />
      {!!countriesData && !!address && (
        <>
          <h3>Edit Address</h3>
          <AddressForm
            handleSaveAddress={handleSaveAddress}
            address={address}
            countries={countriesData.countries}
            saveDisabled={saveDisabled}
          />
        </>
      )}
    </div>
  )
}

const StyledEditAddressContainer = styled(EditAddressContainer)({
  h3: {
    marginBottom: '39px',
  },
})

export default StyledEditAddressContainer
